import { IntlMessageFormat } from 'intl-messageformat'

import { flattenKeys } from './catalog'
import type {
  FlatMessages,
  Formatters,
  Locale,
  Messages,
  MessageValues,
  MoneyFormatOptions,
  TranslateFunction,
  TranslatorMode,
} from './types'

/** Opties voor `createTranslator`. */
export interface TranslatorOptions {
  readonly locale: Locale
  readonly messages: Messages
  /**
   * Overschrijft de modus die uit `NODE_ENV` wordt afgeleid. Alleen bedoeld voor
   * tests: zo is het productiegedrag te controleren zonder `process.env` aan te passen.
   */
  readonly mode?: TranslatorMode
  /** Wordt aangeroepen bij een ontbrekende sleutel, vóór werpen of loggen. */
  readonly onMissingKey?: (key: string, locale: Locale) => void
}

/**
 * Cache van gecompileerde ICU-berichten.
 *
 * De cachesleutel bevat locale, vertaalsleutel én het patroon zelf. Locale en
 * patroon bepalen samen het compilatieresultaat; de vertaalsleutel staat erbij
 * zodat twee namespaces met hetzelfde patroon los van elkaar te volgen zijn en
 * een gewijzigd patroon (hot reload) nooit een oude compilatie hergebruikt.
 */
const messageFormatCache = new Map<string, IntlMessageFormat>()

/** Scheidingsteken in cachesleutels; komt niet voor in sleutels of patronen. */
const CACHE_SEPARATOR = '\u001F'

/** Argumentvorm die intl-messageformat accepteert. */
type IcuValues = Record<string, string | number | boolean | Date | null | undefined>

/** Leegt de compilatiecache. Bedoeld voor tests en hot reload. */
export function clearMessageCache(): void {
  messageFormatCache.clear()
}

/** Aantal gecompileerde berichten in de cache. Bedoeld voor tests en diagnose. */
export function getMessageCacheSize(): number {
  return messageFormatCache.size
}

/**
 * Leidt de actieve modus af uit `NODE_ENV`.
 *
 * `typeof process` wordt gecontroleerd omdat dit bestand ook in de browser draait.
 * Bundlers vervangen `process.env.NODE_ENV` (puntnotatie, letterlijk) door een
 * stringconstante; is er niets bekend, dan geldt productiegedrag — een ontbrekende
 * vertaling mag een uitgeleverde pagina nooit laten crashen.
 */
export function resolveMode(): TranslatorMode {
  // `globalThis` in plaats van de kale `process`-identifier: dit bestand wordt ook
  // door de browserbundels ingelezen, waar `process` niet bestaat en een directe
  // verwijzing bovendien `@types/node` zou afdwingen. Bundlers vervangen alleen de
  // letterlijke puntnotatie, dus in de browser levert dit 'production' op; de twee
  // frontends geven hun modus daarom expliciet mee aan `createTranslator`.
  const host = globalThis as { process?: { env?: Record<string, string | undefined> } }
  const raw = host.process?.env?.['NODE_ENV']
  if (raw === 'development' || raw === 'test') {
    return raw
  }
  return 'production'
}

/**
 * Maakt een vertaalfunctie voor één locale en één berichtenset.
 *
 * De berichten worden bij het aanmaken één keer platgeslagen; het formatteren
 * gebruikt daarna een gedeelde compilatiecache, zodat er niet per render opnieuw
 * gecompileerd wordt.
 */
export function createTranslator(options: TranslatorOptions): TranslateFunction {
  const { locale, messages } = options
  const mode = options.mode ?? resolveMode()
  const flat: FlatMessages = flattenKeys(messages)

  return function translate(key: string, values?: MessageValues): string {
    const pattern = flat[key]
    if (pattern === undefined) {
      return handleMissingKey(key, locale, mode, options.onMissingKey)
    }
    // Snelpad: een bericht zonder argumenten, tags of ICU-escapes hoeft niet
    // gecompileerd te worden. De apostrof staat erbij omdat die in ICU als
    // escapeteken geldt en dus wél door de parser moet.
    if (!pattern.includes('{') && !pattern.includes('<') && !pattern.includes("'")) {
      return pattern
    }
    const formatter = getMessageFormat(locale, key, pattern)
    // intl-messageformat typeert argumenten als PrimitiveType; `bigint` hoort daar
    // niet bij terwijl Intl er wel mee overweg kan (bedragen in centen, §17.6).
    // Vandaar deze twee smalle casts in plaats van `any`.
    const formatted = formatter.format(values as IcuValues | undefined) as string | string[]
    // Rich-text-formattering levert een array op; die voegen we samen tot tekst.
    return Array.isArray(formatted) ? formatted.join('') : formatted
  }
}

function handleMissingKey(
  key: string,
  locale: Locale,
  mode: TranslatorMode,
  onMissingKey: ((key: string, locale: Locale) => void) | undefined,
): string {
  onMissingKey?.(key, locale)
  const message = `Ontbrekende vertaalsleutel "${key}" voor locale "${locale}"`
  if (mode === 'production') {
    // Geen stille terugval op de brontaal (§17.8): loggen en de sleutel tonen,
    // zodat het gat zichtbaar is in plaats van verborgen achter Engelse tekst.
    // eslint-disable-next-line no-console -- deze laag heeft bewust geen afhankelijkheid van de logger van een applicatie; dit is de enige plek waar zij zelf meldt
    console.error(`[i18n] ${message}`)
    return key
  }
  throw new Error(message)
}

function getMessageFormat(locale: Locale, key: string, pattern: string): IntlMessageFormat {
  const cacheKey = `${locale}${CACHE_SEPARATOR}${key}${CACHE_SEPARATOR}${pattern}`
  const cached = messageFormatCache.get(cacheKey)
  if (cached !== undefined) {
    return cached
  }
  const created = new IntlMessageFormat(pattern, locale)
  messageFormatCache.set(cacheKey, created)
  return created
}

// --- Intl-formattering -------------------------------------------------------

const dateTimeFormatCache = new Map<string, Intl.DateTimeFormat>()
const numberFormatCache = new Map<string, Intl.NumberFormat>()
const listFormatCache = new Map<string, Intl.ListFormat>()

const DATE_DEFAULTS: Intl.DateTimeFormatOptions = { dateStyle: 'long' }
const DATE_TIME_DEFAULTS: Intl.DateTimeFormatOptions = { dateStyle: 'long', timeStyle: 'short' }
const TIME_DEFAULTS: Intl.DateTimeFormatOptions = { timeStyle: 'short' }

/**
 * De opties die `Intl.DateTimeFormat` NIET naast `dateStyle` of `timeStyle` accepteert.
 *
 * Dit is geen stijlkwestie maar een harde regel van de specificatie: een stijl is een
 * kant-en-klare vorm, en losse onderdelen ernaast maken de uitkomst dubbelzinnig. De
 * constructor werpt dan `TypeError: Invalid option : option` — een melding die niet
 * zegt wélke optie, en die op het scherm neerkomt als een lege pagina.
 *
 * `timeZone`, `hour12`, `calendar` en dergelijke staan hier bewust NIET tussen: die
 * mogen wel naast een stijl staan, en `{ timeZone: 'UTC' }` is de meest voorkomende
 * aanroep in dit repo.
 */
const COMPONENT_OPTIONS = [
  'weekday',
  'era',
  'year',
  'month',
  'day',
  'dayPeriod',
  'hour',
  'minute',
  'second',
  'fractionalSecondDigits',
  'timeZoneName',
] as const

/**
 * Voegt de standaardstijl samen met wat de aanroeper vraagt.
 *
 * Noemt de aanroeper zelf onderdelen — `{ year: 'numeric', month: 'long' }` — dan
 * bepaalt hij de vorm en verdwijnt de standaardstijl. Dat is precies wat er misging:
 * `{ ...DATE_DEFAULTS, ...options }` hield `dateStyle` erbij, en de combinatie
 * dateStyle+year+month liet het overzichtsscherm bij het laden omvallen. In
 * ontwikkeling viel dat niet op omdat er geen test op stond die een echte
 * `Intl.DateTimeFormat` bouwde met precies die twee samen.
 */
function mergeDateOptions(
  defaults: Intl.DateTimeFormatOptions,
  options: Intl.DateTimeFormatOptions | undefined,
): Intl.DateTimeFormatOptions {
  if (options === undefined) {
    return defaults
  }
  const namesComponents = COMPONENT_OPTIONS.some((name) => options[name] !== undefined)
  if (!namesComponents) {
    return { ...defaults, ...options }
  }
  // De stijl gaat eruit; al het andere uit de standaard (er is er vandaag geen)
  // blijft staan.
  const { dateStyle: _dateStyle, timeStyle: _timeStyle, ...rest } = defaults
  return { ...rest, ...options }
}

/**
 * Intl.NumberFormat versie 3 accepteert een decimale string en behoudt daarmee
 * de volledige precisie. De lib-typings van TypeScript dekken die overload niet
 * bij elk `target`, vandaar deze smalle hulpinterface in plaats van `any`.
 */
interface StringCapableNumberFormat {
  format(value: string): string
}

/** Formatteerfuncties voor één locale, met gedeelde Intl-instanties. */
export function createFormatters(locale: Locale): Formatters {
  const formatDate = (
    value: Date | number | string,
    options?: Intl.DateTimeFormatOptions,
  ): string =>
    getDateTimeFormat(locale, mergeDateOptions(DATE_DEFAULTS, options)).format(toDate(value))

  const formatDateTime = (
    value: Date | number | string,
    options?: Intl.DateTimeFormatOptions,
  ): string =>
    getDateTimeFormat(locale, mergeDateOptions(DATE_TIME_DEFAULTS, options)).format(toDate(value))

  const formatTime = (
    value: Date | number | string,
    options?: Intl.DateTimeFormatOptions,
  ): string =>
    getDateTimeFormat(locale, mergeDateOptions(TIME_DEFAULTS, options)).format(toDate(value))

  const formatNumber = (value: number | bigint, options?: Intl.NumberFormatOptions): string =>
    getNumberFormat(locale, { ...options }).format(value)

  const formatPercent = (value: number, options?: Intl.NumberFormatOptions): string =>
    getNumberFormat(locale, { style: 'percent', ...options }).format(value)

  const formatMoney = (
    cents: bigint | number,
    currency: string,
    options?: MoneyFormatOptions,
  ): string => formatMoneyForLocale(locale, cents, currency, options)

  const formatList = (items: readonly string[], options?: Intl.ListFormatOptions): string =>
    getListFormat(locale, { style: 'long', type: 'conjunction', ...options }).format(items)

  return {
    locale,
    formatDate,
    formatDateTime,
    formatTime,
    formatNumber,
    formatPercent,
    formatMoney,
    formatList,
  }
}

/**
 * Zet centen om naar de notatie van de locale.
 *
 * Bedragen worden als centen opgeslagen (§17.6); dit is uitsluitend presentatie.
 * Het schalen gebeurt met bigint-deling en een decimale string, zodat ook
 * bedragen boven `Number.MAX_SAFE_INTEGER` exact blijven. Het aantal decimalen
 * komt van de valuta zelf (EUR 2, JPY 0), niet van een aanname.
 */
export function formatMoneyForLocale(
  locale: Locale,
  cents: bigint | number,
  currency: string,
  options?: MoneyFormatOptions,
): string {
  const amount = toCents(cents)
  const minorUnits = getCurrencyMinorUnits(locale, currency)
  const formatter = getNumberFormat(locale, { style: 'currency', currency, ...options })
  return (formatter as unknown as StringCapableNumberFormat).format(
    toDecimalString(amount, minorUnits),
  )
}

function toCents(cents: bigint | number): bigint {
  if (typeof cents === 'bigint') {
    return cents
  }
  if (!Number.isInteger(cents)) {
    throw new RangeError(`Bedrag moet een geheel aantal centen zijn, kreeg ${cents}`)
  }
  if (!Number.isSafeInteger(cents)) {
    throw new RangeError(
      `Bedrag ${cents} valt buiten het veilige integerbereik; gebruik een bigint`,
    )
  }
  return BigInt(cents)
}

function toDecimalString(amount: bigint, minorUnits: number): string {
  const negative = amount < 0n
  const absolute = negative ? -amount : amount
  const sign = negative ? '-' : ''
  if (minorUnits === 0) {
    return `${sign}${absolute.toString()}`
  }
  const factor = 10n ** BigInt(minorUnits)
  const whole = absolute / factor
  const fraction = (absolute % factor).toString().padStart(minorUnits, '0')
  return `${sign}${whole.toString()}.${fraction}`
}

function getCurrencyMinorUnits(locale: Locale, currency: string): number {
  // De valuta bepaalt het aantal decimalen; opties van de aanroeper mogen die
  // schaal niet beïnvloeden, anders klopt de omrekening van centen niet meer.
  const resolved = getNumberFormat(locale, { style: 'currency', currency }).resolvedOptions()
  // `maximumFractionDigits` is in de typedefinitie optioneel; twee decimalen is de
  // gangbare schaal wanneer de omgeving niets teruggeeft.
  return resolved.maximumFractionDigits ?? 2
}

function toDate(value: Date | number | string): Date {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) {
    throw new RangeError(`Ongeldige datumwaarde: ${String(value)}`)
  }
  return date
}

function getDateTimeFormat(
  locale: Locale,
  options: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const cacheKey = `${locale}${CACHE_SEPARATOR}${JSON.stringify(options)}`
  const cached = dateTimeFormatCache.get(cacheKey)
  if (cached !== undefined) {
    return cached
  }
  const created = new Intl.DateTimeFormat(locale, options)
  dateTimeFormatCache.set(cacheKey, created)
  return created
}

function getNumberFormat(locale: Locale, options: Intl.NumberFormatOptions): Intl.NumberFormat {
  const cacheKey = `${locale}${CACHE_SEPARATOR}${JSON.stringify(options)}`
  const cached = numberFormatCache.get(cacheKey)
  if (cached !== undefined) {
    return cached
  }
  const created = new Intl.NumberFormat(locale, options)
  numberFormatCache.set(cacheKey, created)
  return created
}

function getListFormat(locale: Locale, options: Intl.ListFormatOptions): Intl.ListFormat {
  const cacheKey = `${locale}${CACHE_SEPARATOR}${JSON.stringify(options)}`
  const cached = listFormatCache.get(cacheKey)
  if (cached !== undefined) {
    return cached
  }
  const created = new Intl.ListFormat(locale, options)
  listFormatCache.set(cacheKey, created)
  return created
}
